import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import { filterPage } from "components/common/Filters/FiltersHelper";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Basictabs from "components/common/Tabs";
import Typography from "components/common/Typography";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { updateRecentlyViewedData } from "../Corp/CorpUser.Slice";
import { fetchBookmarkedInncodeAsync, updateBookmarkedInncodesAsync } from "../dashboard/Dashboard.slice";
import BrandDeadlines from "./BrandDeadlinesAndInitiatives/BrandDeadlines";
import Initiatives from "./BrandDeadlinesAndInitiatives/Initiatives";
import {
  fetchPropertyInfoAsync,
  fetchPropertyPlansCounts,
  reset,
  setOverduePropertyCount
} from "./IndividualProperty.slice";
import Members from "./Members";
import PropertyPlans from "./PropertyPlans";
import "./index.scss";

const Badge = ({ children }: any) => {
  return (
    <Typography
      variant="label"
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "100%",
        backgroundColor: tokens.Icon.Feeback.Alert,
        color: tokens.Text.General.Inverse,
        lineHeight: "20px"
      }}
    >
      {children}
    </Typography>
  );
};

interface Props {
  isIndividualPropertyPage?: boolean;
}

const IndividualProperty = ({ isIndividualPropertyPage }: Props) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { isMobileScreen } = useContext(ThemeContext);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const bookmarkedProperties = useAppSelector((state) => state.aboveProperty.bookmarkedProperties);
  const userSelectedInnCode = useAppSelector((state: any) => state.app.innCode);
  const propertyInfo: any = useAppSelector((state) => state.individualProperty.propertyInfo);
  const {
    propName = "",
    brandName = "",
    innCode = ""
  } = useAppSelector((state) => state.individualProperty.propertyInfo?.[0] || {});

  const tabDetails = [
    {
      label: "Property Plans",
      content: PropertyPlans,
      badge: propertyInfo.overdueMyPlansCount ? <Badge>{propertyInfo.overdueMyPlansCount}</Badge> : null,
      filtersToClear: [filterPage.individualPropertyPlansDisplay]
    },
    {
      label: "Brand Deadlines",
      content: BrandDeadlines,
      badge: propertyInfo.overdueDeadlinePlansCount ? <Badge>{propertyInfo.overdueDeadlinePlansCount}</Badge> : null,
      filtersToClear: [filterPage.individualPropertyBrandDeadlineDisplay]
    },
    { label: "Initiatives", content: Initiatives, filtersToClear: [filterPage.individualPropertyInitiativesDisplay] },
    { label: "Members", content: Members, filtersToClear: [filterPage.individualPropertyMembersDisplay] }
  ];

  const isBookmarked = bookmarkedProperties?.some((b: any) => b?.innCode === params?.inncode);

  useEffect(() => {
    if (isEmpty(bookmarkedProperties)) dispatch(fetchBookmarkedInncodeAsync({}));
    if (isCorp) {
      dispatch(
        updateRecentlyViewedData({
          property: [
            {
              innCode: params?.inncode
            }
          ]
        })
      );
    }

    return () => {
      const overdueCount = {
        overdueDeadlinePlansCount: 0,
        overdueMyPlansCount: 0
      };
      dispatch(reset());
      //  dispatch(resetAbovePropertySlice());
      dispatch(setOverduePropertyCount(overdueCount));
    };
  }, []);

  useEffect(() => {
    dispatch(fetchPropertyInfoAsync({ inncodes: params.inncode || userSelectedInnCode }));
  }, [userSelectedInnCode]);

  useEffect(() => {
    (async () => {
      const dataPayload = {
        page: 0,
        payload: {
          innCode: params.inncode || userSelectedInnCode,
          includeOverdueMyPlansCount: true,
          includeOverdueDeadlinePlansCount: true
        }
      };
      const res = await dispatch(fetchPropertyPlansCounts(dataPayload));
      const count = res?.payload?.meta;
      const overdueCount = {
        overdueDeadlinePlansCount: count.overDueDeadlinePlansCount,
        overdueMyPlansCount: count.overDueMyPlansCount
      };
      dispatch(setOverduePropertyCount(overdueCount));
    })();
  }, [userSelectedInnCode]);

  const handleBookmarking = async (isbookmark: boolean) => {
    if (isbookmark) {
      await dispatch(
        updateBookmarkedInncodesAsync({
          bookmarks: bookmarkedProperties?.filter((item: any) => item?.innCode !== params?.inncode)
        })
      );
    } else {
      await dispatch(
        updateBookmarkedInncodesAsync({
          bookmarks: [
            ...bookmarkedProperties,
            {
              innCode: params?.inncode
            }
          ]
        })
      );
    }
    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        variant: "success",
        setState: setFeedbackDetails({ shouldShow: false }),
        message: isbookmark ? "Property removed from bookmarked." : "Property added to bookmarked."
      })
    );
  };

  const onTabChangeFn = () => {
    dispatch(reset());
  };

  return (
    <>
      <Stack
        spacing={1}
        direction={isMobileScreen ? "column" : "row"}
        className={isIndividualPropertyPage ? "individualProperty" : ""}
      >
        <Box sx={{ flex: 1, maxWidth: isMobileScreen ? "100%" : "528px" }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} sx={{ mb: "12px" }}>
              {brandName}
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            showBold
            color={tokens.Text.General.PrimaryHeadline}
            sx={{
              mb: "16px"
            }}
          >
            {propName ?? innCode}
          </Typography>
        </Box>
        {isIndividualPropertyPage && (
          <Box sx={{ flex: 1, textAlign: isMobileScreen ? "center" : "right" }}>
            <Button
              label={
                <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
                  {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  <Typography
                    showBold
                    variant={"body2"}
                    children={isBookmarked ? "Remove from Bookmarked" : "Add to Bookmarked"}
                  ></Typography>
                </Box>
              }
              variant="secondary"
              sx={isMobileScreen ? { width: "100%", mt: "32px" } : {}}
              accessType={isImpersonated ? accessTypes.update : accessTypes.view}
              onClick={() => handleBookmarking(isBookmarked)}
            />
          </Box>
        )}
      </Stack>
      <Box>
        <Basictabs
          alignItems="left"
          marginBottom="0"
          tabDetails={tabDetails}
          onTabChangeFn={onTabChangeFn}
          isParentTab={isIndividualPropertyPage}
          isChildTab={!isIndividualPropertyPage}
          // skipClearFilter={true}
        />
      </Box>
    </>
  );
};

export default IndividualProperty;
